<script setup lang="ts">
import type { OrderLineFragment } from '#graphql-operations'
import { upperFirst } from 'scule'

const props = defineProps<{
  loading?: boolean
}>()

const emit = defineEmits<{
  (event: 'addToCart'): void
  (event: 'change:orderLine', orderLine: OrderLineFragment, quantity: number): void
}>()

const { t } = useI18n()
const activeOrderStore = useActiveOrderStore()
const { loading } = toRefs(props)

const isMounted = ref(false)
const productStore = useProductStore()

const activeOrderLine = computed(() => activeOrderStore.activeOrder.data?.lines.find(line => line.productVariant.id === productStore.activeVariant?.id))

const productOptions = computed(() => activeOrderLine.value?.productVariant.options.map(option => upperFirst(option.name)).join('/'))

const quantity = computed(() => activeOrderLine.value?.quantity.toString() ?? '')

const quantities = computed(() => Array.from({ length: 99 }, (_, i) => (i + 1).toString()))

function handleQuantityChange(qty: string) {
  if (qty === quantity.value || !activeOrderLine.value)
    return
  emit('change:orderLine', activeOrderLine.value, +qty)
}

const extraLabels = computed(() => {
  const labels = []

  if (productStore.discountPercentage > 0) {
    labels.push({
      icon: 'i-ri:discount-percent-line',
      text: t('general.discount', [`${productStore.discountPercentage}%`]),
    })
  }

  return labels
})

onMounted(() => {
  isMounted.value = true
})
</script>

<template>
  <div class="flex flex-row flex-nowrap items-center justify-start">
    <div class="wfull flex flex-1 flex-row flex-nowrap items-center justify-start">
      <div
        v-if="!isMounted || activeOrderStore.activeOrder.status === 'pending' || loading" class="flex-1"
        :aria-label="t('general.add_to_cart.label')"
        role="button"
      >
        <div
          class="relative z1 h12 wfull flex flex-col select-none items-center justify-center text-center text-base text-black fw-600 !cursor-not-allowed"
          role="button"
        >
          <span class="bg-main-300 absolute inset-0 inline-block rd-sm transition-all duration-150 -z1 n-active:bg-op40" />
          <svg
            class="mr-3 h-4 w-4 animate-spin text-white -ml-1"
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path
              class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>

      <div
        v-else-if="isMounted && activeOrderLine"
        class="bg-main-300 relative h12 wfull flex flex-row items-center justify-between rd-sm text-black fw-bold leading-12 transition-all duration-150 ease-linear"
      >
        <div
          class="hfull w-[20%] flex flex-row cursor-pointer items-center justify-center rd-sm active:rd-sm active:bg-[hsla(0,0%,100%,.3)] hover:rd-sm hover:bg-[hsla(0,0%,100%,.2)]"
          @click="emit('change:orderLine', activeOrderLine, activeOrderLine.quantity - 1)"
        >
          <Icon name="i-mdi:minus" class="text-xl" />
        </div>

        <RdxSelectRoot :model-value="quantity" @update:model-value="handleQuantityChange">
          <RdxSelectTrigger
            class="hfull w-[60%] flex flex-col cursor-pointer items-center justify-center of-hidden text-ellipsis whitespace-nowrap text-black fw-bold leading-4 active:rd-sm active:bg-[hsla(0,0%,100%,.3)] hover:rd-sm hover:bg-[hsla(0,0%,100%,.2)]"
          >
            <RdxSelectValue class="wfull">
              <div class="wfull text-center">
                {{ activeOrderLine.quantity }}
                <span class="ml0.75">
                  {{ t('cart.added') }}
                </span>
              </div>
              <div
                class="mx-auto mb0 mtpx max-w-[90%] of-hidden text-ellipsis whitespace-nowrap text-center text-xs text-black fw600"
              >
                {{ productOptions }}
              </div>
            </RdxSelectValue>
          </RdxSelectTrigger>

          <RdxSelectPortal>
            <RdxSelectContent
              position="popper"
              class="z100 h-[--reka-popper-available-height] max-h-[236px] min-w-[160px] w-[--reka-popper-anchor-width] rounded-sm bg-white shadow-lg transition-opacity,transform will-change-opacity,transform data-[side=bottom]:animate-[slideUpAndFade_150ms] data-[side=left]:animate-[slideRightAndFade_150ms] data-[side=right]:animate-[slideLeftAndFade_150ms] data-[side=top]:animate-[slideDownAndFade_150ms]"
            >
              <RdxScrollAreaRoot class="hfull wfull" type="auto">
                <RdxSelectViewport as-child>
                  <RdxScrollAreaViewport class="hfull wfull">
                    <RdxSelectItem
                      v-for="qty in quantities"
                      :key="qty"
                      class="relative mr1.75 h9.5 flex select-none items-center pl3 data-[highlighted]:bg-slate-100 data-[highlighted]:outline-none n-checked:bg-slate-100 n-text-body data-[disabled]:n-disabled"
                      :value="qty"
                    >
                      <RdxSelectItemIndicator
                        class="absolute right-0 w-[25px] inline-flex items-center justify-center"
                      >
                        <Icon name="i-ri:check-line" />
                      </RdxSelectItemIndicator>
                      <RdxSelectItemText>
                        {{ qty }}
                      </RdxSelectItemText>
                    </RdxSelectItem>
                  </RdxScrollAreaViewport>
                </RdxSelectViewport>

                <RdxScrollAreaScrollbar class="w1.75 p1.75" orientation="vertical">
                  <RdxScrollAreaThumb class="rd-[3px] bg-slate-200 !w1.75" />
                </RdxScrollAreaScrollbar>
              </RdxScrollAreaRoot>
            </Rdxselectcontent>
          </RdxSelectPortal>
        </RdxSelectRoot>

        <div
          class="hfull w-[20%] flex flex-row cursor-pointer items-center justify-center rd-sm active:rd-sm active:bg-[hsla(0,0%,100%,.3)] hover:rd-sm hover:bg-[hsla(0,0%,100%,.2)]"
          @click="emit('change:orderLine', activeOrderLine, activeOrderLine.quantity + 1)"
        >
          <Icon name="i-ri:add-fill" class="text-xl" />
        </div>
      </div>

      <button
        v-else
        class="flex-1"
        :aria-label="t('general.add_to_cart.label')"
        role="button"
        data-testid="addToCartButton"
        @click="emit('addToCart')"
      >
        <div
          class="group relative z1 h12 wfull flex flex-col cursor-pointer select-none items-center justify-center text-center text-base text-black fw-600"
          role="button"
        >
          <span
            class="bg-main-300 group-hover:bg-main-300/90 absolute inset-0 inline-block rd-sm transition-all duration-150 -z1 group-hover:scale-102 n-active:bg-op40"
          />

          {{ t('general.add_to_cart.label') }}

          <div class="max-w-full text-center transition-all duration-200 hidden">
            <div flex="~ row wrap" class="relative items-center justify-center gap2">
              <template v-for="(label, index) in extraLabels" :key="index">
                <div
                  class="relative flex-row flex-wrap items-center justify-center whitespace-normal border-solid text-xs leading-0"
                  :class="{
                    'hidden xs:flex': index === 0,
                  }"
                >
                  <Icon :name="label.icon" class="text-0.75rem" />
                  <span class="shrink-0 self-center text-black fw600">
                    {{ label.text }}
                  </span>
                </div>
                <div v-if="index === 0" class="h2 wpx bg-white/50 hidden xs:block" />
              </template>
            </div>
          </div>
        </div>
      </button>
    </div>
  </div>
</template>
